<template>
  <div class="w-full flex flex-col">
    <div class="mb-2.5">
      <ul class="text-sm mb-2.5 flex flex-col gap-1">
        <li>ACFM = airflow in actual cubic feet per minute</li>
        <li>P = gage pressure (psi)</li>
        <li>T = gas temperature °R = 460 + °F</li>
        <li>SCFM = airflow in standard cubic feet per minute</li>
      </ul>
      <div class="font-bold text-center">Fill in 3 values to determine the SCFM</div>
    </div>
    <div class="w-full flex flex-col gap-1">
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">ACFM</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.acfm}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.acfm}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.acfm}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.acfm) }"
            v-model="state.acfm" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">P</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.pact}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.pact}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.pact}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.pact) }"
            v-model="state.pact" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">T (°R)</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.tact}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.tact}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.tact}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.tact) }"
            v-model="state.tact" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">SCFM</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.scmf}-input`"
          class="w-full pointer-events-none">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.scmf}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.scmf}-input`"
            placeholder=""
            class="w-full h-8 border border-slate-300 text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.scmf) }"
            v-model="state.scmf" />
        </label>
      </div>
      <div class="w-full flex items-center justify-center px-4 gap-2.5 mt-1">
        <div
          class="text-sm py-1.5 px-3 rounded text-white bg-base-color hover:bg-dark-base-color cursor-pointer flex items-center justify-center transition-colors duration-300"
          :class="{ 'grayscale pointer-events-none': !(state.acfm !== '' || state.pact !== '' || state.tact !== '') }"
          @click="resetAction">
          Reset
        </div>
        <div
          class="w-full max-w-44 text-sm py-1.5 px-3 rounded text-white bg-base-color hover:bg-dark-base-color cursor-pointer flex items-center justify-center transition-colors duration-300"
          :class="{ 'grayscale pointer-events-none': !state.isFormValid }"
          @click="calculateAction">
          Calculate
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, watch } from 'vue';
import GlobalHelper from '@/helpers/global/global.helper';

enum CalculatorFieldsTypes {
  acfm = 'acfm',
  pact = 'pact',
  tact = 'tact',
  scmf = 'scmf',
}

const calcType: string = 'acfm-to-scfm-calculator';

const state = reactive({
  acfm: '' as string | number,
  pact: '' as string | number,
  tact: '' as string | number,
  scmf: '' as string | number,
  isFormValid: false as boolean,
  inputFields: [CalculatorFieldsTypes.acfm, CalculatorFieldsTypes.pact, CalculatorFieldsTypes.tact] as string[],
});

const checkIsFormValid = () => {
  state.isFormValid = state.acfm !== '' && state.pact !== '' && state.tact !== '';
};

const resetAction = () => {
  state.acfm = '';
  state.pact = '';
  state.tact = '';
  state.scmf = '';
};

const calculateAction = () => {
  if (state.isFormValid) {
    state.scmf = GlobalHelper.roundToDecimals(Number(state.acfm) * ((Number(state.pact) + 14.7) / 14.7) * (528 / Number(state.tact)), 4);
  }
};

watch(
  () => state.acfm,
  () => {
    checkIsFormValid();
  }
);

watch(
  () => state.pact,
  () => {
    checkIsFormValid();
  }
);

watch(
  () => state.tact,
  () => {
    checkIsFormValid();
  }
);
</script>
